import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { OrdersService } from '../shared/orders.service';
import { Label } from 'tns-core-modules';
import { MapsAPILoader } from '@agm/core';

interface Peaje {
  Nombre: string;
  Lat: number;
  Lng: number;
  Valor: number;
}

interface ResumenRuta {
  distanciaKm: number;
  duracionMin: number;
  peajes: number;
}

export interface PuntoIntermedio {
  lat: number;
  lng: number;
  peaje: number;
}

interface RutaRequest {
  horaInicio: string;
  resumen: ResumenRuta;
  peajes: Peaje[];
}

export interface Vehicle {
  id: string;
  start: [number, number]; // [lon, lat]
  end?: [number, number];  // Opcional
}

export interface Waypoint {
  id: string;
  location: [number, number];
}

export interface OptimizationRequest {
  version: number;
  options: {
    roundtrip: boolean;
    source?: string;
    destination?: string;
  };
  vehicles: Vehicle[];
  waypoints: Waypoint[];
}

export interface OptimizationResponse {
  code: string;
  waypoints: Array<{
    id: string;
    vehicle_id: string;
    waypoint_index: number;
  }>;
  trips: Array<{
    vehicle_id: string;
    geometry: string;
    distance: number;
    duration: number;
    waypoints: number[];
  }>;
}

@Injectable({
  providedIn: 'root'
})

export class ResumenTareasService {
  private apiUrl = 'https://us-central1-appvlbogota-43289.cloudfunctions.net/app';
  private apiUrlmapbox = 'https://api.mapbox.com/optimized-trips/v1';
  private accessToken = 'sk.eyJ1IjoidnJ0LWFwcCIsImEiOiJjbThwYTY0ejcwOG1pMmtwc2dwczBlNzgyIn0.g_7RAIZMl9gG2hgZwh-HOw';


  private resumenSubject = new BehaviorSubject<any | null>(null);
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  private ubicacionesSubject = new BehaviorSubject<any | null>(null);
  resumen$: Observable<any> = this.resumenSubject.asObservable();
  resumenUbi$: Observable<any> = this.ubicacionesSubject.asObservable();
  isLoading$: Observable<boolean> = this.isLoadingSubject.asObservable();
  private consultar = new Subject<{ inicio: string, fin: string }>();
  consultar$ = this.consultar.asObservable();


  constructor(private http: HttpClient, private orderService: OrdersService, private mapsAPILoader: MapsAPILoader) { }
  private apiKeyHepe = '9oZR5QIeDIOZJgusBRD1JLI-qo-n4vq5cH5dFLINodc';
  baseUrl = 'https://geocode.search.hereapi.com/v1/geocode';

  getTripStatus(tripId: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/token-optimized-trip/${tripId}`);
  }


  getOptimizedTrip(requestBody: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/optimized-trip`, requestBody);
  }

  optimizeRoute(coordinates: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/optimize-route-mapbox?coordinates=${coordinates}`);
  }
  optimizeRoutev1(coordinates: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/optimize-route-mapbox?coordinates=${coordinates}`);
  }

  searchPlaces(params: any): Observable<any> {
    // Convertir el objeto de parámetros a HttpParams
    let httpParams = new HttpParams();
    Object.keys(params).forEach(key => {
      httpParams = httpParams.append(key, params[key]);
    });

    return this.http.get(`${this.apiUrl}/searchPlaces`, { params: httpParams });
  }

  searchPlacesDetails(query: string, extraParams: any = {}): Observable<any> {
    // Configurar parámetros
    let params = new HttpParams()
      .set('query', query)
      .set('fields', 'place_id,name,vicinity,formatted_address,address_components,geometry,rating,types,opening_hours,permanently_closed,business_status,url,website,formatted_phone_number,international_phone_number,user_ratings_total,price_level,reviews');

    // Agregar parámetros adicionales
    Object.keys(extraParams).forEach(key => {
      params = params.append(key, extraParams[key]);
    });

    return this.http.get(`${this.apiUrl}/searchPlacesSinDetails`, { params });
  }

  getPlaceDetails(placeId: string): Observable<any> {
    const params = new HttpParams().set('place_id', placeId);
    return this.http.get(`${this.apiUrl}/place-details`, { params });
  }

  // Método para realizar la solicitud HTTP
  getResumenTareas(negocio: string, agencia: string, startDate: string, endDate: string): Observable<any> {
    // Activa la bandera de carga
    this.isLoadingSubject.next(true);

    // Realiza la solicitud HTTP
    return this.http.get<any>(`${this.apiUrl}/getResumenTareas/${negocio}/${agencia}/${startDate}/${endDate}`).pipe(
      tap((data) => {
        this.resumenSubject.next(data); // Guarda la nueva respuesta en el BehaviorSubject
        this.isLoadingSubject.next(false); // Desactiva la bandera de carga

      }),
      catchError((error) => {
        this.isLoadingSubject.next(false); // Desactiva la bandera de carga en caso de error
        throw error; // Propaga el error para que los suscriptores lo manejen
      })
    );
  }

  // Método para obtener los datos actuales (sin realizar una nueva solicitud HTTP)
  getCurrentResumen(): Observable<any> {
    return this.resumen$; // Devuelve el Observable del BehaviorSubject
  }

  getUbicaciones(negocio: string, agencia: string, date: string): Observable<any> {
    // Activa la bandera de carga
    this.isLoadingSubject.next(true);

    // Realiza la solicitud HTTP
    return this.http.get<any>(`${this.apiUrl}/v2/getUbicacionDiaRuta/${negocio}_${agencia}/${date}`).pipe(
      tap((data) => {
        this.ubicacionesSubject.next(data); // Guarda la nueva respuesta en el BehaviorSubject
        this.isLoadingSubject.next(false); // Desactiva la bandera de carga

      }),
      catchError((error) => {
        this.isLoadingSubject.next(false); // Desactiva la bandera de carga en caso de error
        throw error; // Propaga el error para que los suscriptores lo manejen
      })
    );
  }

  getModelos(negocio?: string, agencia?: string): Observable<any> {
    let params = new HttpParams();
    if (negocio) {
      params = params.set('negocio', negocio);
    }
    if (agencia) {
      params = params.set('agencia', agencia);
    }
    return this.http.get<any>(`${this.apiUrl}/getModel`, { params });
  }

  deletePlanilla(fecha: string, negocio: string, ordenDeTransporte: string): Observable<any> {
    const url = `${this.apiUrl}/deleteOrdenTransporte/${fecha}/${negocio}/${ordenDeTransporte}`;
    return this.http.get(url); // <-- Aquí estás usando GET porque tu backend usa GET
    // Si fuera DELETE (mejor práctica), sería: this.http.delete(url);
  }

  eliminarModelo(id: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/deleteModel/${id}`);
  }

  deleteTareas(fecha: string, negocio: string, ordenDeTransporte: string): Observable<any> {
    const url = `${this.apiUrl}/deleteTareas/${fecha}/${negocio}/${ordenDeTransporte}`;
    return this.http.get(url); // <-- Aquí estás usando GET porque tu backend usa GET
    // Si fuera DELETE (mejor práctica), sería: this.http.delete(url);
  }

  getUbicacionesUsuario(negocio: string, agencia: string, date: string, usaurio: string): Observable<any> {
    // Activa la bandera de carga
    this.isLoadingSubject.next(true);

    // Realiza la solicitud HTTP
    return this.http.get<any>(`${this.apiUrl}/v2/getUbicacionDiaRutaUsuarioDos/${negocio}_${agencia}/${date}/${usaurio}`).pipe(
      tap((data) => {
        this.ubicacionesSubject.next(data); // Guarda la nueva respuesta en el BehaviorSubject
        this.isLoadingSubject.next(false); // Desactiva la bandera de carga

      }),
      catchError((error) => {
        this.isLoadingSubject.next(false); // Desactiva la bandera de carga en caso de error
        throw error; // Propaga el error para que los suscriptores lo manejen
      })
    );
  }
  getUbicacionesResumen(): Observable<any> {
    return this.resumenUbi$; // Devuelve el Observable del BehaviorSubject
  }

  private apiKey = 'AIzaSyCyd2VvB-GnsyowcGGQZiGLYU3e2rIMeew'; // Reemplaza con tu clave de API


  getRoute(origin: string, destination: string, waypoints: string[]) {
    const waypointsStr = waypoints.join('|');
    const params = {
      origin: origin,
      destination: destination,
      waypoints: waypointsStr,
    };

    return this.http.get(`${this.apiUrl}/directionsApi/`, { params });
  }

  optimizeRoutes(shipments: any[], pickups: any[]): Observable<any> {

    const data = {
      model: {
        shipments,
        vehicles: [
          {
            label: "uno",
            startLocation: {
              latitude: 6.2553554,
              longitude: -75.5807607
            },
            endLocation: {
              latitude: 6.2553554,
              longitude: -75.5807607
            },
            costPerKilometer: 1.0,
            loadLimits: {
              units: {
                "maxLoad": "20"
              }
            },
          },
          {
            label: "dos",
            startLocation: {
              latitude: 6.2553554,
              longitude: -75.5807607
            },
            endLocation: {
              latitude: 6.2553554,
              longitude: -75.5807607
            },
            costPerKilometer: 1.0,
            loadLimits: {
              units: {
                "maxLoad": "20"
              }
            }
          },
          {
            label: "tres",
            startLocation: {
              latitude: 6.2553554,
              longitude: -75.5807607
            },
            endLocation: {
              latitude: 6.2553554,
              longitude: -75.5807607
            },
            costPerKilometer: 1.0,
            loadLimits: {
              units: {
                "maxLoad": "20"
              }
            }
          },
          {
            label: "cuatro",
            startLocation: {
              latitude: 6.2553554,
              longitude: -75.5807607
            },
            endLocation: {
              latitude: 6.2553554,
              longitude: -75.5807607
            },
            costPerKilometer: 1.0,
            loadLimits: {
              units: {
                "maxLoad": "20"
              }
            }
          },
          {
            label: "cinco",
            startLocation: {
              latitude: 6.2553554,
              longitude: -75.5807607
            },
            endLocation: {
              latitude: 6.2553554,
              longitude: -75.5807607
            },
            costPerKilometer: 1.0,
            loadLimits: {
              units: {
                "maxLoad": "20"
              }
            }
          },
          {
            label: "seis",
            startLocation: {
              latitude: 6.2553554,
              longitude: -75.5807607
            },
            endLocation: {
              latitude: 6.2553554,
              longitude: -75.5807607
            },
            costPerKilometer: 1.0,
            loadLimits: {
              units: {
                "maxLoad": "40"
              }
            }
          }
        ],
        globalStartTime: "2026-03-23T06:00:00.000Z",
        globalEndTime: "2026-03-23T23:00:00.000Z"
      }
    };
    console.log('✅ loca del data:', data);

    return this.http.post(`${this.apiUrl}/demo-optimize-route`, data);
  }

  createClientGeo(modelo: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/createClientGeo`, modelo);
  }

  clientPlaceID(modelo: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/clientPlaceID`, modelo);
  }

  createlistClientGeo(modelo: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/createListClientGeo`, modelo);
  }


  guardarModelo(modelo: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/createModel`, modelo);
  }

  validateGeo(direccion: String): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/v2/validateGeoIA`, { direccion });
  }

  consultarNegocio(nombreLegal: string, direccion: string, departamento: string, nombreNoLegal: string, direccioninversa: string): Observable<any> {
    const params = new HttpParams()
      .set('nombreLegal', nombreLegal)
      .set('direccion', direccion)
      .set('departamento', departamento)
      .set('direccioninversa', direccioninversa)
      .set('nombreNoLegal', nombreNoLegal);

    return this.http.get(`${this.apiUrl}/consultar-negocio`, { params });
  }

  searchNearbyPlaces(lat: number, lng: number, radius: number, type?: string): Observable<any> {
    let params = new HttpParams()
      .set('lat', lat.toString())
      .set('lng', lng.toString())
      .set('radius', radius.toString());

    if (type) {
      params = params.set('type', type);
    }

    return this.http.get(`${this.apiUrl}/searchPlacesGeo`, { params });
  }

  searchByCoordinates(lat: number, lng: number, radius?: number, query?: string, type?: string): Observable<any> {
    let params = new HttpParams()
      .set('lat', lat.toString())
      .set('lng', lng.toString());

    if (radius) params = params.set('radius', radius.toString());
    if (query) params = params.set('query', query);
    if (type) params = params.set('type', type);

    return this.http.get(`${this.apiUrl}/searchByCoordinates`, { params });
  }

  searchPlacesRaw(
    lat?: number,
    lng?: number,
    radius?: number,
    type?: string,
    pageToken?: string
  ): Observable<any> {
    let params = new HttpParams()

    if (pageToken) {
      params = params.set('pagetoken', pageToken);
    } else if (lat && lng && radius) {
      params = params
        .set('lat', lat.toString())
        .set('lng', lng.toString())
        .set('radius', radius.toString());

      if (type) params = params.set('type', type);
    }

    return this.http.get(`${this.apiUrl}/searchWithFormattedAddressSin`, { params });
  }

  getInfoEmpresa(nit: string): Observable<any> {
    const params = new HttpParams().set('nit', nit);

    return this.http.get(`${this.apiUrl}/republica-info`, { params });
  }

  geocodeAddress(address: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.mapsAPILoader.load().then(() => {
        const geocoder = new google.maps.Geocoder();

        geocoder.geocode({ address }, (results, status) => {
          if (status === 'OK' && results.length > 0) {
            //const direcciones = results.map(result => result.formatted_address);
            resolve(results);
          } else {
            reject(`Geocoding failed: ${status}`);
          }
        });
      });
    });
  }

  geocodeLatLng(lat: number, lng: number): Promise<any> {
    return new Promise((resolve, reject) => {
      this.mapsAPILoader.load().then(() => {
        const geocoder = new google.maps.Geocoder();
        const latlng = { lat, lng };

        geocoder.geocode({ location: latlng }, (results, status) => {
          if (status === 'OK' && results.length > 0) {
            //const direcciones = results.map(result => result.formatted_address);
            resolve(results);
          } else {
            reject(`Reverse geocoding failed: ${status}`);
          }
        });
      });
    });
  }



  getRouteApi(originLat: number, originLng: number, destLat: number, destLng: number): Observable<any> {
    const params = new HttpParams()
      .set('originLat', originLat.toString())
      .set('originLng', originLng.toString())
      .set('destLat', destLat.toString())
      .set('destLng', destLng.toString());

    return this.http.get<any>(`${this.apiUrl}/routesApi`, { params });
  }

  guardarRecurso(recurso: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/createRecurso`, recurso);
  }

  getRecursos(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/getRecurso`);
  }

  eliminarRecurso(id: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/deleteRecurso/${id}`);
  }

  guardarEsquema(esquema: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/createEsquema`, esquema);
  }

  guardarValidateGeo(esquema: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/validateGeo`, esquema);
  }

  getEsquemas(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/getEsquema`);
  }

  eliminarEsquema(id: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/deleteEsquema/${id}`);
  }

  normalizarDireccion(direccion: string): Observable<any> {

    return this.http.get<any>(`${this.apiUrl}/normalizarDireccion/${direccion}`);
  }

  validateAddress(address: string, locality: string = "", regionCode: string = 'CO'): Observable<any> {
    // Usando parámetros en la URL (GET)
    const params = new HttpParams()
      .set('address', address)
      .set('locality', locality)
      .set('regionCode', regionCode);

    return this.http.get(`${this.apiUrl}/validateAddress`, { params });

  }

  getCoordinates(address: string): Observable<any> {

    const encodedAddress = encodeURIComponent(address); // Codifica la dirección correctamente
    const url = `${this.baseUrl}?q=${encodedAddress}&apiKey=${this.apiKeyHepe}`;

    return this.http.get(url);
  }

  getTruckRoute(originLat: number, originLng: number, destLat: number, destLng: number): Observable<any> {
    const url = `https://router.hereapi.com/v8/routes` +
      `?transportMode=truck` +
      `&origin=${originLat},${originLng}` +
      `&destination=${destLat},${destLng}` +
      `&return=summary,tolls,polyline` +
      `&apikey=${this.apiKeyHepe}`;

    return this.http.get(url);
  }

  buscarTareasPorDireccion(
    negocio: string,
    agencia: string,
    direccion: string,
    startDate: string,
    endDate: string
  ): Observable<any> {
    console.log("ulr enviada direccion", direccion)
    const url = `${this.apiUrl}/getResumenTareasDireccion/${negocio}/${agencia}/${encodeURIComponent(direccion)}/${startDate}/${endDate}`;
    console.log("ulr enviada", url)
    return this.http.get(url);
  }

  updateGeoValidationByAddress(address: string, data: { status: boolean, distance: number, score: number, validateDistance: number }) {
    return this.http.put(`${this.apiUrl}/updateValidateGeoByAddress`, {
      address,
      ...data
    });
  }


  updateGeoByAddress(address: string, data: { status: boolean, distance: number, score: number, validateDistance: number, geoGoogle: any, geoHere: any, validate: any, address: string, latitude: number, longitude: number }) {
    return this.http.put(`${this.apiUrl}/updateGeoByAddress`, {
      address,
      ...data
    });
  }

  guardarListaGeo(esquema: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/createListControlGeo`, esquema);
  }

  consultadireccionmaps(datos: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post(`${this.apiUrl}/consulta-direccion-maps`, datos, { headers });
  }

  generarItinerario(data: RutaRequest): Observable<any> {
    return this.http.post(`${this.apiUrl}/generar-itinerario-ruta`, data);
  }


  getRuta(origen: string, destino: string, puntosIntermedios: PuntoIntermedio[]): Observable<any> {
    const body = { origen, destino, puntosIntermedios };
    return this.http.post(`${this.apiUrl}/rutaPuntosIntermedios`, body);
  }

  analizarTopografiaTramos(tramos: any[]): Observable<any> {
    return this.http.post<any[]>(`${this.apiUrl}/clasificar-topografia-tramos`, { tramos });
  }

  buscarLugares(texto: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/busquedatexto`, { texto });
  }

  detalleLugar(placeId: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/detalle-lugar`, { place_id: placeId });
  }


  createRutaViajero(ruta: any): Observable<any> {
    if (!ruta || ruta.length === 0) {
      console.error('La lista de rutas está vacía');
    }

    const primeraRuta = ruta[0];
    console.log('Enviando primera ruta', primeraRuta);

    return this.http.post(`${this.apiUrl}/createRutaViajero`, primeraRuta);
  }



  createRutaViajeroPlanilla(rutas: any[], guia: string): Observable<any> {
    const payload = { guia, rutas };
    return this.http.post(`${this.apiUrl}/createRutaViajeroPlanilla`, payload);
  }

  createRutaViajeroPeajes(rutas: any[], guia: string): Observable<any> {
    const payload = { guia, rutas };
    return this.http.post(`${this.apiUrl}/createRutaViajeroPeajes`, payload);
  }

  createRutaViajeroDetalle(rutas: any, guia: string): Observable<any> {
    const payload = { guia, rutas };
    return this.http.post(`${this.apiUrl}/createRutaViajeroDetalle`, payload);
  }

  createHereViajero(rutas: any, guia: string): Observable<any> {
    const payload = { guia, rutas };
    return this.http.post(`${this.apiUrl}/createHereViajero`, payload);
  }



  getRutasViajeros(negocio: string, agencia: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/getRutasViajeros`, {
      params: {
        negocio,
        agencia
      }
    });
  }

  getRutaViajeroPorGuia(guia: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/getHereViajero/${guia}`);

  }
  getRutasViajerosPlanilla(guia: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/getRutasViajerosPlanilla/${guia}`);

  }

  getRutasViajerosDetalle(guia: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/getRutasViajerosDetalle/${guia}`);

  }


  getRutasViajerosPeajes(guia: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/getRutasViajerosPeajes/${guia}`);

  }

  getDetalleLugar(placeId: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/detalle-lugar`, { place_id: placeId });
  }

  getSearchToll(lat: number, lng: number, radius: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/searchToll?lat=${lat}&lng=${lng}&radius=${radius}`);
  }
}